import { useTranslation } from 'react-i18next';
import { CalendarPlus, CheckCheck, LockOpen, Pause, Play, Power, RotateCcw, X } from 'lucide-react';
import type { DeviceType } from '@gc/shared';

import { bulkActionsForType, sharedBulkActions, type BulkActionId } from './deviceCapabilities';

export type { BulkActionId };

/**
 * The bulk-action bar over the board's checkbox selection (wave 3 #12, #6-UI).
 * The checkboxes have existed since wave 1 writing into a Set nothing read —
 * this bar is that Set's first reader.
 *
 * Buttons are permission-gated with the SAME keys the API enforces, so
 * nothing here dangles a 403: power -> `devices.manage`, support unlock ->
 * `hotkey.unlock`, reserve/pause/resume -> `sessions.manage`.
 *
 * Presentation: wide colour-coded buttons that each carry the count, rather
 * than a row of identical grey pills. Six same-looking pills meant reading
 * every label to find one, and — worse — nothing distinguished "Pause" from
 * "Shut down" until it had already run on the selection. Colour here is
 * semantic, not decoration: brand for the booking action, the board's own
 * status colours for play/pause, danger red for the one that cuts power.
 */

/** Per-action tone. Foregrounds are pinned dark/light for contrast on the fill. */
const TONE: Record<BulkActionId, { button: string; badge: string }> = {
  reserve: { button: 'bg-primary text-primary-fg hover:bg-primary-hover', badge: 'bg-black/20' },
  play: { button: 'bg-available text-[#04220f] hover:brightness-110', badge: 'bg-black/15' },
  pause: { button: 'bg-ending text-[#2e1c02] hover:brightness-110', badge: 'bg-black/15' },
  supportUnlock: { button: 'bg-busy text-[#04162e] hover:brightness-110', badge: 'bg-black/15' },
  restart: {
    button: 'border border-strong bg-overlay text-content hover:bg-hover',
    badge: 'bg-white/10',
  },
  shutdown: { button: 'bg-danger text-white hover:bg-danger-hover', badge: 'bg-black/20' },
};
export function SelectionBar({
  count,
  visibleCount,
  selectedTypes,
  can,
  onClear,
  onSelectAllVisible,
  onAction,
}: {
  /**
   * Selected devices that are VISIBLE under the current filters — never the
   * raw selection-set size. `count < visibleCount` below decides whether
   * "Select all" still has work to do, and that comparison only means
   * anything when both numbers describe the same set of tiles (R4#8).
   */
  count: number;
  visibleCount: number;
  /**
   * The device types in the selection. Actions are offered only where every
   * selected device can carry them out — see `deviceCapabilities.ts`.
   */
  selectedTypes: readonly DeviceType[];
  /** Injected so the permission matrix is testable without an auth fetch. */
  can: (permission: string) => boolean;
  onClear: () => void;
  onSelectAllVisible: () => void;
  onAction: (action: BulkActionId) => void;
}) {
  const { t } = useTranslation();
  if (count === 0) return null;

  const actions: Array<{
    id: BulkActionId;
    permission: string;
    icon: React.ReactNode;
    labelKey: string;
  }> = [
    {
      id: 'reserve',
      permission: 'sessions.manage',
      icon: <CalendarPlus className="h-4 w-4" aria-hidden />,
      labelKey: 'deviceBoard.bulk.reserve',
    },
    // Play before Pause, which is the order these two sit in on every remote
    // control and on the system the venue is replacing.
    //
    // `discounts.apply`, not `sessions.manage`: Play opens a machine FREE, and
    // giving the room away is the privilege the server already gates free mode
    // on. A button that 403s when pressed is a promise the system cannot keep.
    {
      id: 'play',
      permission: 'discounts.apply',
      icon: <Play className="h-4 w-4 fill-current" aria-hidden />,
      labelKey: 'deviceBoard.bulk.play',
    },
    {
      id: 'pause',
      permission: 'sessions.manage',
      icon: <Pause className="h-4 w-4 fill-current" aria-hidden />,
      labelKey: 'deviceBoard.bulk.pause',
    },
    {
      id: 'supportUnlock',
      permission: 'hotkey.unlock',
      icon: <LockOpen className="h-4 w-4" aria-hidden />,
      labelKey: 'deviceBoard.bulk.supportUnlock',
    },
    {
      id: 'restart',
      permission: 'devices.manage',
      icon: <RotateCcw className="h-4 w-4" aria-hidden />,
      labelKey: 'deviceBoard.bulk.restart',
    },
    {
      id: 'shutdown',
      permission: 'devices.manage',
      icon: <Power className="h-4 w-4" aria-hidden />,
      labelKey: 'deviceBoard.bulk.shutdown',
    },
  ];

  // Two independent gates, and an action must clear both: what this employee
  // may do, and what these devices can actually be asked to do.
  const capable = sharedBulkActions(selectedTypes);
  const permitted = new Set(actions.filter((action) => can(action.permission)).map((a) => a.id));
  const granted = actions.filter(
    (action) => permitted.has(action.id) && capable.includes(action.id),
  );

  /**
   * True only when MIXING cost the user something: some selected type would
   * have offered a permitted action that the intersection withholds. A
   * selection of two pool tables is narrow because pool tables are narrow, not
   * because it is mixed, and saying otherwise trains staff to ignore the line.
   */
  const narrowedByMixing = selectedTypes.some((deviceType) =>
    bulkActionsForType(deviceType).some(
      (action) => permitted.has(action) && !capable.includes(action),
    ),
  );

  return (
    <div
      data-selection-bar
      className="sticky bottom-0 z-30 border-t border-strong bg-raised px-4 pb-3 pt-2 shadow-[0_-8px_24px_rgba(0,0,0,0.45)]"
    >
      {/* Housekeeping line: what is selected, and the two ways out of it. */}
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-2 text-sm font-semibold text-content">
          <span className="grid h-5 w-5 place-items-center rounded-full bg-primary text-[11px] font-bold text-primary-fg tabular">
            {count}
          </span>
          {t('deviceBoard.selection.selected', { count })}
        </span>

        <div className="flex-1" />

        {count < visibleCount ? (
          <button
            type="button"
            onClick={onSelectAllVisible}
            className="inline-flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary transition-colors hover:bg-hover hover:text-content"
          >
            <CheckCheck className="h-3.5 w-3.5" aria-hidden />
            {t('deviceBoard.selection.selectAllVisible', { count: visibleCount })}
          </button>
        ) : null}

        <button
          type="button"
          onClick={onClear}
          className="inline-flex items-center gap-1.5 rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary transition-colors hover:border-danger/60 hover:bg-hover hover:text-danger"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
          {t('deviceBoard.selection.clear')}
        </button>
      </div>

      {/*
        The actions themselves: wide enough to hit without aiming, each one
        repeating the count so "Shut down" can never be pressed while thinking
        it applies to one machine when it applies to eleven.
      */}
      <div className="flex flex-wrap gap-2">
        {granted.map((action) => (
          <button
            key={action.id}
            type="button"
            data-bulk-action={action.id}
            onClick={() => onAction(action.id)}
            className={[
              'inline-flex min-w-[8.5rem] flex-1 items-center justify-center gap-2',
              'rounded-control px-3 py-2.5 text-sm font-semibold transition-all',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-content/40',
              TONE[action.id].button,
            ].join(' ')}
          >
            {action.icon}
            <span className="truncate">{t(action.labelKey)}</span>
            <span
              className={`tabular rounded-full px-1.5 py-0.5 text-[11px] font-bold ${TONE[action.id].badge}`}
            >
              {count}
            </span>
          </button>
        ))}
      </div>

      {/*
        Say why, rather than letting buttons vanish silently — someone who ticks
        a pool table on top of ten PCs and watches Shut down disappear will
        otherwise assume the board broke.
      */}
      {narrowedByMixing ? (
        <p data-selection-hint className="mt-2 text-xs text-content-muted">
          {t('deviceBoard.selection.mixedTypes')}
        </p>
      ) : null}
    </div>
  );
}
